/**
 * Lightweight CSV + JSON export helpers.
 *
 * Usage:
 *   exportCSV("products.csv", rows, ["title", "price", "category"]);
 *   <ExportButtons rows={products} filename="products" columns={[...]} />
 */
import { Download, FileText, FileJson } from "lucide-react";

export function toCSV(rows, columns) {
    if (!rows || !rows.length) return "";
    const cols = columns && columns.length ? columns : Object.keys(rows[0]);
    const escape = (val) => {
        if (val == null) return "";
        let s = typeof val === "object" ? JSON.stringify(val) : String(val);
        if (s.includes(",") || s.includes('"') || s.includes("\n")) {
            s = `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };
    const header = cols.join(",");
    const body = rows
        .map((r) => cols.map((c) => escape(getDeep(r, c))).join(","))
        .join("\n");
    return `${header}\n${body}`;
}

function getDeep(obj, path) {
    if (!path) return obj;
    return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}

function downloadBlob(content, filename, mime) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 200);
}

export function exportCSV(filename, rows, columns) {
    const csv = toCSV(rows, columns);
    downloadBlob(csv, filename.endsWith(".csv") ? filename : `${filename}.csv`, "text/csv;charset=utf-8;");
}

export function exportJSON(filename, rows) {
    downloadBlob(
        JSON.stringify(rows, null, 2),
        filename.endsWith(".json") ? filename : `${filename}.json`,
        "application/json"
    );
}

/** Print a simple printable HTML table — user can "Save as PDF" from browser print dialog. */
export function exportPrintablePDF(title, rows, columns) {
    if (!rows || !rows.length) return;
    const cols = columns && columns.length ? columns : Object.keys(rows[0]);
    const html = `<!doctype html>
<html><head><title>${title}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 24px; color: #121214; }
  h1 { font-family: "Playfair Display", Georgia, serif; font-weight: 600; }
  table { width:100%; border-collapse: collapse; margin-top: 16px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #E7DED2; text-align: left; font-size: 12px; }
  th { background: #FAF7F1; text-transform: uppercase; font-size: 10px; letter-spacing: .12em; color: #6B645C; }
  .meta { color: #857C72; font-size: 11px; }
</style></head>
<body>
  <h1>${title}</h1>
  <p class="meta">Generated ${new Date().toLocaleString()} • ${rows.length} rows</p>
  <table>
    <thead><tr>${cols.map((c) => `<th>${c}</th>`).join("")}</tr></thead>
    <tbody>${rows
        .map(
            (r) =>
                `<tr>${cols
                    .map((c) => `<td>${(getDeep(r, c) ?? "").toString().replace(/[<>&]/g, (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m]))}</td>`)
                    .join("")}</tr>`
        )
        .join("")}</tbody>
  </table>
  <script>window.onload = () => window.print();</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
}

/** Reusable export buttons row. */
export function ExportButtons({ rows = [], filename = "export", columns, className = "" }) {
    const disabled = !rows || rows.length === 0;
    return (
        <div className={`inline-flex items-center gap-1.5 ${className}`} data-testid="export-buttons">
            <button
                type="button"
                disabled={disabled}
                onClick={() => exportCSV(filename, rows, columns)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui disabled:opacity-50"
                data-testid="export-csv-btn"
                title="Download CSV"
            >
                <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button
                type="button"
                disabled={disabled}
                onClick={() => exportPrintablePDF(filename, rows, columns)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui disabled:opacity-50"
                data-testid="export-pdf-btn"
                title="Download PDF (opens print preview)"
            >
                <FileText className="h-3.5 w-3.5" /> PDF
            </button>
            <button
                type="button"
                disabled={disabled}
                onClick={() => exportJSON(filename, rows)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui disabled:opacity-50"
                data-testid="export-json-btn"
                title="Download JSON"
            >
                <FileJson className="h-3.5 w-3.5" /> JSON
            </button>
            <span className="ml-1 text-[10px] font-mono-ui text-[#857C72]" data-testid="export-count">
                {rows.length} row{rows.length === 1 ? "" : "s"}
            </span>
        </div>
    );
}
